<script setup lang="ts">
import { cloneDeep } from 'lodash-es';
import { computed, ref, shallowRef, watch } from 'vue';

import { CustomColumnProps, DragableColumn } from './CustomColumn.vue.d';
import { setVisibilityByMaxColumn } from './helpers/setVisibilityColumn.helper';
import Checkbox from '../checkbox/Checkbox.vue';
import { TableColumn } from '../datatable/DataTable.vue.d';
import useDataTableStore, {
  DataTableColumnConfig,
} from '../datatable/store/dataTable.store';
import Icon from '../icon/Icon.vue';

const props = defineProps<
  CustomColumnProps & { savedColumns: DragableColumn[] }
>();

const model = defineModel<TableColumn[]>();

/**
 * Clone of default columns to be used in reorder and toggle visibility without mutating the default columns.
 * This `columnReorderData` ref will store the reordered table columns.
 *
 * On component loaded, we need to load the config to re-order the column list in the ColumnVisibiliy overlay menu.
 */
const columnReorderData = ref<DragableColumn[]>([]);

const draggedColumn = shallowRef<DragableColumn>();

const filteredVisibleColumn = computed<DragableColumn[]>(() => {
  return [...columnReorderData.value].filter((col) => col.visible !== false);
});

const hasReachMaxColumn = computed<boolean>(() => {
  if (!props.maxColumn) return false;
  return filteredVisibleColumn.value.length >= props.maxColumn;
});

const dragable = (item: TableColumn): boolean => {
  return item.dragable !== false && isSelectAble(item);
};

const isSelectAble = (item: TableColumn): boolean => {
  if (!hasReachMaxColumn.value) return true;

  const isChecked = model.value.findIndex((c) => c.field === item.field) > 0;

  /**
   * The checkbox should be selectable if:
   * - Selected column has not reach the max column
   * - reach the max column and the checkbox is currently checked (to uncheck).
   */
  return hasReachMaxColumn.value && isChecked;
};

const groupHeaders = (columns: DragableColumn[]): DragableColumn[] => {
  const grouped: DragableColumn[] = [];

  for (const column of columns) {
    if (column.parentHeader) {
      /*
       * Only include one column with the same parent header
       */
      const matchParentColumn = grouped.find(
        (group) => group.parentHeaderField === column.parentHeader.field,
      );

      if (!matchParentColumn) {
        grouped.push({
          ...column,
          parentHeaderField: column.parentHeader.field,
          siblingHeaders: [],
        });
      } else {
        matchParentColumn.siblingHeaders.push(column);
      }
    } else {
      grouped.push(column);
    }
  }

  return grouped;
};

const ungroupHeaders = (columns: DragableColumn[]): TableColumn[] => {
  const ungrouped: TableColumn[] = [];

  for (const column of columns) {
    if (column.parentHeaderField) {
      ungrouped.push(column, ...column.siblingHeaders);
    } else {
      ungrouped.push(column);
    }
  }

  return ungrouped;
};

const updateVisibleColumnsModel = (): void => {
  columnReorderData.value = setVisibilityByMaxColumn(
    columnReorderData.value,
    props.maxColumn,
    filteredVisibleColumn.value.length,
  );

  model.value = ungroupHeaders([...filteredVisibleColumn.value]);
};

const setColumnVisibilityConfig = async (): Promise<void> => {
  const columnsConfig: DataTableColumnConfig[] = ungroupHeaders(
    columnReorderData.value,
  ).map((col) => {
    return {
      field: col.field,
      width: col.width ?? 'max-content',
      visible: col.visible !== false,
      fixed: !!col.fixed,
    };
  });

  const { setConfig } = await useDataTableStore();
  await setConfig(props.tableId, columnsConfig);
};

const reorderVisibleColumn = (): void => {
  model.value = ungroupHeaders([...columnReorderData.value]);
};

/**
 * Starts the reordering of columns. Fired when user drag an item.
 *
 * @param {DragEvent} event - The drag event object.
 * @param {DragableColumn} item - The column item being dragged.
 * @param {number} index - The index of the column item being dragged.
 */
const startReorderColumn = (
  event: DragEvent,
  item: DragableColumn,
  index: number,
): void => {
  const { dataTransfer } = event;
  draggedColumn.value = item;

  if (dataTransfer) {
    dataTransfer.dropEffect = 'move';
    dataTransfer.effectAllowed = 'move';
    dataTransfer.setData('field', item.field);
    dataTransfer.setData('index', index.toString());
  }
};

/**
 * Handles the reordering of columns. Fired when user release the pointer.
 */
const onColumnReorder = async (): Promise<void> => {
  draggedColumn.value = undefined;
  reorderVisibleColumn();
  await setColumnVisibilityConfig();
};

const onDragEnter = (e: DragEvent, col: DragableColumn): void => {
  e.preventDefault(); // <-- Still essential

  if (dragable(col)) {
    const target = e.target as HTMLElement;
    const dropTargetLi =
      target.tagName === 'LI' ? target : target.closest('li');

    dropTargetLi?.classList.add('dark:bg-grayscale-800');
    dropTargetLi?.classList.add('bg-primary-50');
    dropTargetLi?.nextElementSibling?.classList.remove('dark:bg-grayscale-800');
    dropTargetLi?.nextElementSibling?.classList.remove('bg-primary-50');
    dropTargetLi?.previousElementSibling?.classList.remove(
      'dark:bg-grayscale-800',
    );
    dropTargetLi?.previousElementSibling?.classList.remove('bg-primary-50');

    if (!dropTargetLi || !draggedColumn.value) return; // Safety checks

    const draggedIndex = columnReorderData.value.findIndex(
      (item) => item.field === draggedColumn.value?.field,
    );

    const dropTargetIndex = columnReorderData.value.findIndex(
      (item) => item.field === dropTargetLi.id,
    );

    if (draggedIndex !== -1 && dropTargetIndex !== -1) {
      // Swap the items in columnReorderData
      [
        columnReorderData.value[draggedIndex],
        columnReorderData.value[dropTargetIndex],
      ] = [
        columnReorderData.value[dropTargetIndex],
        columnReorderData.value[draggedIndex],
      ];
    }
  }
};

/**
 * Click event handler on `li` element to checks the checkbox insides.
 * @param {MouseEvent} event - The mouse event object.
 */
const checkTheCheckbox = (event: MouseEvent): void => {
  const label = (event.target as HTMLElement).querySelector('label');

  if (label) label.click();
};

/**
 * Resets the column visibility to default.
 */
const resetToDefault = (): void => {
  const defaultColumnClone: TableColumn[] = cloneDeep(props.defaultColumns);

  if (props.maxColumn && defaultColumnClone.length > props.maxColumn) {
    let visibleColumnsLength = 0;
    columnReorderData.value = groupHeaders(
      defaultColumnClone.map((column) => {
        const spaceAvailable =
          props.maxColumn !== undefined &&
          visibleColumnsLength < props.maxColumn;

        const visible =
          (column.checkedByDefault !== false &&
            props.maxColumn === undefined) ||
          (column.checkedByDefault !== false && spaceAvailable);

        if (visible) visibleColumnsLength++;

        return {
          ...column,
          visible,
        };
      }),
    );
  } else {
    columnReorderData.value = groupHeaders(defaultColumnClone);
  }

  model.value = ungroupHeaders([...filteredVisibleColumn.value]);
  setColumnVisibilityConfig();
};

watch(
  () => props.savedColumns,
  (newVal) => {
    columnReorderData.value = groupHeaders(newVal) ?? [];
  },
  { immediate: true },
);

defineExpose({
  setColumnVisibilityConfig,
  resetToDefault,
  updateVisibleColumnsModel,
});
</script>

<template>
  <ul>
    <template v-for="(item, index) in columnReorderData">
      <li
        :id="item.field"
        :key="item.field"
        v-if="item.reorderable !== false"
        v-bind="
          $preset.customcolumn.menuitem({
            props: props,
            context: { dragable: dragable(item) },
          })
        "
        :data-item-dragable="dragable(item)"
        :draggable="dragable(item)"
        @click="checkTheCheckbox"
        @dragend="onColumnReorder"
        @dragenter.prevent="onDragEnter($event, item)"
        @dragleave.prevent=""
        @dragover.prevent=""
        @dragstart="startReorderColumn($event, item, index)"
        @mouseenter="
          ($event.target as HTMLLIElement).classList.add('bg-primary-50');
          ($event.target as HTMLLIElement).classList.add(
            'dark:bg-grayscale-800',
          );
        "
        @mouseleave="
          ($event.target as HTMLLIElement).classList.remove('bg-primary-50');
          ($event.target as HTMLLIElement).classList.remove(
            'dark:bg-grayscale-800',
          );
        "
        data-wv-section="custom-column-menu-item"
      >
        <Icon
          @click.stop=""
          v-bind="
            $preset.customcolumn?.dragicon({
              context: { dragable: dragable(item) },
            })
          "
          icon="dragable-menu"
        />

        <Checkbox
          :aria-label="item.header"
          :disabled="item.fixed"
          :label="item.parentHeader?.header ?? item.header"
          :model-value="item.visible !== false"
          :selectable="isSelectAble(item)"
          @click.stop=""
          @update:model-value="
            (event) => {
              item.visible = !!event;
              item.siblingHeaders?.forEach((sibling) => {
                sibling.visible = !!event;
              });
              updateVisibleColumnsModel();
              setColumnVisibilityConfig();
            }
          "
          class="!w-full"
        />
      </li>
    </template>
  </ul>
</template>
